import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { fr } from "./locales/fr";
import { en } from "./locales/en";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type Language = "fr" | "en";

export interface LanguageOption {
  code: Language;
  label: string;
  flagCode: string;
  flagEmoji: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: "fr", label: "Français", flagCode: "fr", flagEmoji: "🇫🇷" },
  { code: "en", label: "English", flagCode: "gb", flagEmoji: "🇬🇧" },
];

const dictionaries: Record<Language, typeof fr> = {
  fr,
  en,
};

interface I18nContextValue {
  lang: Language;
  setLanguage: (lang: Language) => void;
  t: (path: string, params?: Record<string, string | number>) => string;
  dict: typeof fr;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = "bizconnectpay_lang";

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "fr" || saved === "en") return saved;
      // Auto-detect browser language if available
      const navLang = navigator.language?.toLowerCase() || "";
      if (navLang.startsWith("en")) return "en";
    } catch {
      // Fallback
    }
    return "fr";
  });

  const setLanguage = useCallback((newLang: Language) => {
    setLangState(newLang);
    try {
      localStorage.setItem(STORAGE_KEY, newLang);
      document.documentElement.lang = newLang;
    } catch {
      // Ignore storage errors
    }
  }, []);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch {
      // ignore
    }
  }, [lang]);

  const t = useCallback(
    (path: string, params?: Record<string, string | number>): string => {
      const dict = dictionaries[lang] || dictionaries.fr;
      const keys = path.split(".");
      let val: unknown = dict;

      for (const k of keys) {
        if (
          val &&
          typeof val === "object" &&
          k in (val as Record<string, unknown>)
        ) {
          val = (val as Record<string, unknown>)[k];
        } else {
          // Fallback to French if missing in current dictionary
          let fallbackVal: unknown = dictionaries.fr;
          for (const fk of keys) {
            if (
              fallbackVal &&
              typeof fallbackVal === "object" &&
              fk in (fallbackVal as Record<string, unknown>)
            ) {
              fallbackVal = (fallbackVal as Record<string, unknown>)[fk];
            } else {
              fallbackVal = path;
              break;
            }
          }
          val = fallbackVal;
          break;
        }
      }

      if (typeof val !== "string") {
        return path;
      }

      if (params) {
        return Object.entries(params).reduce(
          (acc, [pk, pv]) =>
            acc.replace(new RegExp(`\\{${pk}\\}`, "g"), String(pv)),
          val,
        );
      }

      return val;
    },
    [lang],
  );

  return (
    <I18nContext.Provider
      value={{
        lang,
        setLanguage,
        t,
        dict: dictionaries[lang] || dictionaries.fr,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Provide a safe default if used outside Provider (e.g. unit tests)
    return {
      lang: "fr",
      setLanguage: () => {},
      t: (path: string, params?: Record<string, string | number>) => {
        const keys = path.split(".");
        let val: unknown = dictionaries.fr;
        for (const k of keys) {
          if (
            val &&
            typeof val === "object" &&
            k in (val as Record<string, unknown>)
          ) {
            val = (val as Record<string, unknown>)[k];
          } else {
            return path;
          }
        }
        if (typeof val !== "string") return path;
        if (params) {
          return Object.entries(params).reduce(
            (acc, [pk, pv]) =>
              acc.replace(new RegExp(`\\{${pk}\\}`, "g"), String(pv)),
            val,
          );
        }
        return val;
      },
      dict: dictionaries.fr,
    };
  }
  return ctx;
}

/**
 * Flag component for Language (FR / GB)
 */
export function LanguageFlag({
  code,
  className,
}: {
  code: Language;
  className?: string;
}) {
  const flagCode = code === "fr" ? "fr" : "gb";
  return (
    <span
      aria-hidden
      className={cn(
        `fi fi-${flagCode} shrink-0 rounded-[2px] shadow-xs`,
        className,
      )}
      style={{ width: "1.25em", height: "0.95em", display: "inline-block" }}
    />
  );
}

/**
 * Switcher dropdown button with flag icons for both languages.
 */
export function LanguageSwitcher({
  className,
  variant = "topbar",
}: {
  className?: string;
  variant?: "topbar" | "auth" | "inline";
}) {
  const { lang, setLanguage } = useI18n();
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-switcher"
        aria-label="Changer de langue / Change language"
        className={cn(
          "inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40",
          variant === "topbar" &&
            "text-white/90 hover:bg-white/10 hover:text-white",
          variant === "auth" &&
            "border border-slate-200 bg-white/90 text-slate-700 shadow-xs hover:bg-white hover:text-slate-900",
          variant === "inline" &&
            "border border-slate-200 bg-card text-foreground hover:bg-accent",
          className,
        )}
      >
        <LanguageFlag code={current.code} className="text-sm" />
        <span className="uppercase">{current.code}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        {LANGUAGES.map((item) => (
          <DropdownMenuItem
            key={item.code}
            data-testid={`lang-option-${item.code}`}
            onClick={() => setLanguage(item.code)}
            className={cn(
              "flex cursor-pointer items-center justify-between gap-2 text-xs font-medium",
              lang === item.code && "font-bold text-primary",
            )}
          >
            <div className="flex items-center gap-2">
              <LanguageFlag code={item.code} className="text-sm" />
              <span>{item.label}</span>
            </div>
            {lang === item.code && (
              <span className="text-xs text-primary">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
